/* ============================================================
   Interactive scroll-jacked couch.

   Behavior:
     - The host section (#the-couch) is 100vh tall and lives in
       normal page flow.
     - When the section's top reaches the viewport top we CAPTURE
       wheel / touch / keyboard scroll events:
         * scroll-down with progress < 1       -> preventDefault, advance progress
         * scroll-up   with 0 < progress < 1   -> preventDefault, rewind progress
         * anything else                       -> let the page scroll
     - The page literally does not scroll while the couch is
       zooming. Only when progress hits 0 (scrolling up) or 1
       (scrolling down) does normal scrolling resume.
   ============================================================ */
(function () {
	// 'use strict';

	const section   = document.getElementById('the-couch');
	const couchEl   = document.getElementById('the-3d-couch');
	const stage     = document.getElementById('the-3d-couch__stage');
	const couchWrap = document.getElementById('the-3d-couch__couch-wrap');
	const overlay   = document.getElementById('the-3d-couch__overlay');
	const bg        = document.getElementById('the-3d-couch__bg');
	const hint      = document.getElementById('the-3d-couch__hint');

	if (!section || !couchEl || !stage || !couchWrap) { return; }

	/* ----------------------- Tunables ----------------------- */
	const COUCH_START_SCALE = 1.0;
	const COUCH_MAX_SCALE   = 1.5;  // hard cap on the end-state zoom
	const BG_DIM_MAX        = 0.7;
	const BG_BLUR_MAX_PX    = 6;

	// How fast wheel/touch motion advances the animation TARGET.
	// One pixel of input moves the target by this much.
	const PROGRESS_PER_WHEEL_PX = 1 / 500;
	const PROGRESS_PER_TOUCH_PX = 1 / 250;

	// How aggressively the rendered progress chases the target on
	// each animation frame. 0..1; higher = snappier. 0.12 ≈ ~½s to
	// close most of a full-range gap at 60fps.
	const SMOOTHING       = 0.12;
	const SMOOTH_EPSILON  = 0.0005;

	// How close to the section's top edge (in px) counts as "in the
	// jack zone". Wider than 0 so we don't lose capture from a single
	// frame of momentum scroll.
	const JACK_ZONE_PX = 30;

	// Couch bounding box within the source frame (in %).
	const COUCH_LEFT   = 35.7;
	const COUCH_RIGHT  = 92.7;
	const COUCH_TOP    = 20;
	const COUCH_BOTTOM = 88;

	const COUCH_CENTER_X = (COUCH_LEFT + COUCH_RIGHT) / 2;
	const COUCH_CENTER_Y = (COUCH_TOP + COUCH_BOTTOM) / 2;
	const COUCH_REL_W = (COUCH_RIGHT - COUCH_LEFT) / 100;
	const COUCH_REL_H = (COUCH_BOTTOM - COUCH_TOP) / 100;
	const CENTER_SHIFT_X = 50 - COUCH_CENTER_X;
	const CENTER_SHIFT_Y = 50 - COUCH_CENTER_Y;
	const COUCH_END_FILL = 0.95;

	couchWrap.style.transformOrigin = COUCH_CENTER_X + '% ' + COUCH_CENTER_Y + '%';

	// End scale: would normally fill ~95% of the viewport, but capped
	// at COUCH_MAX_SCALE so the couch can't grow more than 2.5x its
	// in-room size regardless of viewport dimensions.
	function computeEndScale() {
		const canvasEl = stage.querySelector('.the-3d-couch__canvas');
		const vw = window.innerWidth;
		const vh = window.innerHeight;
		const canvasW = canvasEl ? canvasEl.getBoundingClientRect().width : Math.min(vw, vh * 1.5);
		const canvasH = canvasW / 1.5;
		const scaleForWidth  = (COUCH_END_FILL * vw) / (canvasW * COUCH_REL_W);
		const scaleForHeight = (COUCH_END_FILL * vh) / (canvasH * COUCH_REL_H);
		return Math.min(scaleForWidth, scaleForHeight, COUCH_MAX_SCALE);
	}
	let COUCH_END_SCALE = computeEndScale();

	function clamp01(v) { return Math.max(0, Math.min(1, v)); }
	function lerp(a, b, t) { return a + (b - a) * t; }
	function easeInOut(t) { return t < 0.5 ? 2*t*t : 1 - Math.pow(-2*t + 2, 2) / 2; }

	/* ----------------------- Animation -----------------------
	   Two state variables:
	     animTarget   - what the input (wheel/touch/key) has asked for
	     animProgress - what is currently rendered on screen
	   On each animation frame we ease animProgress toward animTarget,
	   then render based on animProgress. This smooths out jumpy
	   wheel deltas — a single big wheel-flick still produces a
	   continuous zoom because the renderer interpolates over many
	   frames toward the new target.
	*/
	let animTarget   = 0;
	let animProgress = 0;
	let rafId        = null;

	function render() {
		const t = easeInOut(animProgress);
		const sc = lerp(COUCH_START_SCALE, COUCH_END_SCALE, t);
		const tx = CENTER_SHIFT_X * t;
		const ty = CENTER_SHIFT_Y * t;
		couchWrap.style.transform =
			'translate(' + tx + '%, ' + ty + '%) scale(' + sc + ')';
		if (overlay) { overlay.style.opacity = lerp(0, BG_DIM_MAX, t); }
		if (bg)      { bg.style.filter = 'blur(' + lerp(0, BG_BLUR_MAX_PX, t) + 'px)'; }
		if (hint)    { hint.style.opacity = 1 - clamp01(animProgress / 0.3); }
	}

	function tick() {
		const diff = animTarget - animProgress;
		if (Math.abs(diff) > SMOOTH_EPSILON) {
			animProgress += diff * SMOOTHING;
			rafId = requestAnimationFrame(tick);
		} else {
			animProgress = animTarget;
			rafId = null;
		}
		render();
	}

	function startTicking() {
		if (rafId !== null) { return };
		rafId = requestAnimationFrame(tick);
	}

	/* ----------------------- Scroll-jack -----------------------
	   The lock state replaces the old per-event pinToSection().
	   When animTarget enters (0, 1) we add a class on <html> that
	   sets overflow:hidden — the page literally can't scroll,
	   not even from inertia. When animTarget hits an endpoint
	   we remove the class.

	   Scroll-jack is DISABLED below the desktop breakpoint
	   (matches the CSS at 64rem). Mobile gets a static image
	   with native page scrolling — see desktopMQL.matches check
	   in consume().
	*/
	const htmlEl = document.documentElement;
	const desktopMQL = window.matchMedia('(min-width: 64rem)');
	let isLocked = false;

	function isInJackZone() {
		const rect = section.getBoundingClientRect();
		return rect.top <= JACK_ZONE_PX && rect.top >= -JACK_ZONE_PX;
	}

	function setLocked(shouldLock) {
		if (shouldLock === isLocked) { return; }
		isLocked = shouldLock;
		if (shouldLock) {
			// One-time snap to section top so the section is in
			// perfect alignment for the duration of the animation.
			window.scrollTo(0, section.offsetTop);
			htmlEl.classList.add('couch-scroll-locked');
		} else {
			htmlEl.classList.remove('couch-scroll-locked');
		}
	}

	// Consume a delta in px (positive = scroll-down direction).
	// Updates the TARGET; the render loop eases progress toward it.
	// Returns true if we captured (i.e. the event should be
	// preventDefaulted).
	function consume(deltaPx, perPx) {
		// Below the desktop breakpoint, scroll-jack is off entirely
		// (mobile shows a static image and scrolls natively).
		if (!desktopMQL.matches) { return false; }

		// Once locked, the page can't scroll — accept input from
		// anywhere on the page. Otherwise only accept input when
		// the section is at the viewport top.
		if (!isLocked && !isInJackZone()) { return false; }

		if (deltaPx > 0 && animTarget < 1) {
			// Starting a fresh forward run? Sync progress to 0 in
			// case the previous reset's ease hasn't completed yet.
			// Without this, an incoming small target (e.g. 0.2)
			// against a lagging progress (e.g. 0.7) would make
			// progress decrease while the user scrolls down —
			// the "shake" we saw before.
			if (animTarget === 0 && animProgress > 0) {
				animProgress = 0;
				if (rafId !== null) {
					cancelAnimationFrame(rafId);
					rafId = null;
				}
			}
			animTarget = Math.min(1, animTarget + deltaPx * perPx);
			setLocked(animTarget > 0 && animTarget < 1);
			startTicking();
			return true;
		}
		if (deltaPx < 0 && animTarget > 0 && animTarget < 1) {
			animTarget = Math.max(0, animTarget + deltaPx * perPx);
			setLocked(animTarget > 0 && animTarget < 1);
			startTicking();
			return true;
		}
		return false;
	}

	/* ----- Wheel ----- */
	window.addEventListener('wheel', function (e) {
		if (consume(e.deltaY, PROGRESS_PER_WHEEL_PX)) {
			e.preventDefault();
		}
	}, { passive: false });

	/* ----- Touch (mobile) ----- */
	let touchPrevY = null;
	window.addEventListener('touchstart', function (e) {
		if (e.touches && e.touches[0]) { touchPrevY = e.touches[0].clientY; }
	}, { passive: true });

	window.addEventListener('touchmove', function (e) {
		if (touchPrevY === null || !e.touches || !e.touches[0]) { return; }
		const y = e.touches[0].clientY;
		const dy = touchPrevY - y;     // positive = swipe up = scroll down
		touchPrevY = y;
		if (consume(dy, PROGRESS_PER_TOUCH_PX)) {
			e.preventDefault();
		}
	}, { passive: false });

	window.addEventListener('touchend', function () { touchPrevY = null; }, { passive: true });

	/* ----- Keyboard ----- */
	const SCROLL_KEYS = {
		'ArrowDown':  +120,
		'ArrowUp':    -120,
		'PageDown':   +800,
		'PageUp':     -800,
		' ':          +600,
		'Spacebar':   +600
	};
	window.addEventListener('keydown', function (e) {
		if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) { return; }
		const delta = SCROLL_KEYS[e.key];
		if (delta === null) { return; }
		if (consume(delta, PROGRESS_PER_WHEEL_PX)) {
			e.preventDefault();
		}
	});

	/* ----- Scroll listener (reset only) -----
	   With overflow:hidden during the animation, scroll events
	   only fire when the page is actually scrolling (i.e. we're
	   NOT locked). This listener only does the reset:
	   if the user has scrolled back above the section, set
	   animTarget to 0 and let progress smoothly ease back. The
	   re-entry shake is prevented separately, inside consume()
	   — when a fresh wheel-down captures and progress hasn't
	   caught up yet, consume() snaps progress to 0.
	*/
	window.addEventListener('scroll', function () {
		if (isLocked) { return; }
		const rect = section.getBoundingClientRect();
		if (rect.top > JACK_ZONE_PX && animTarget !== 0) {
			animTarget = 0;
			startTicking();   // progress eases back to 0
		}
	}, { passive: true });

	/* ----- Resize keeps end-scale accurate ----- */
	window.addEventListener('resize', function () {
		COUCH_END_SCALE = computeEndScale();
		startTicking();
	});

	/* ----- Breakpoint change — release if going to mobile -----
	   If the viewport shrinks below the desktop breakpoint while
	   we're mid-animation or locked, fully release: unlock the
	   page scroll, cancel any tick, and reset target+progress
	   to 0 so mobile starts clean. The mobile scroll listener
	   below will then re-compute the target from the couch's
	   current position in the viewport.
	*/
	function handleBreakpointChange() {
		if (desktopMQL.matches) { return; }
		if (isLocked) { setLocked(false); }
		animTarget = 0;
		animProgress = 0;
		if (rafId !== null) {
			cancelAnimationFrame(rafId);
			rafId = null;
		}
		render();
		// Re-sync from the couch's current viewport position so
		// e.g. resizing on a page already mid-section doesn't snap
		// to "small" if the couch is currently in view.
		updateMobileTarget();
	}
	if (desktopMQL.addEventListener) {
		desktopMQL.addEventListener('change', handleBreakpointChange);
	} else if (desktopMQL.addListener) {
		desktopMQL.addListener(handleBreakpointChange);   // legacy Safari
	}

	/* ----- Mobile: scroll-driven progress -----
	   On mobile the scroll-jack is off and we don't use a
	   discrete in-view trigger (that fires intersection-out at
	   awkward moments). Instead, animTarget is computed
	   continuously from the couch's position in the viewport
	   on each scroll event:

	     rect.top >= viewportH  -> target 0 (couch below view)
	     rect.top <= 0          -> target 1 (couch top reached
	                                viewport top — fully zoomed)
	     in between             -> linear interpolation

	   Because target clamps at 1 as soon as rect.top <= 0, a
	   user who keeps scrolling past the section stays at full
	   zoom — no jerk-back. The animation only retreats when
	   they scroll BACK UP and the couch slides back down into
	   the lower half of the viewport.
	*/
	function updateMobileTarget() {
		if (desktopMQL.matches) { return; }
		const rect = couchEl.getBoundingClientRect();
		const viewportH = window.innerHeight;
		const ratio = 1 - (rect.top / viewportH);
		const newTarget = Math.max(0, Math.min(1, ratio));
		if (Math.abs(newTarget - animTarget) > 0.001) {
			animTarget = newTarget;
			startTicking();
		}
	}

	window.addEventListener('scroll', updateMobileTarget, { passive: true });

	// Initial paint (also sets the initial mobile target if applicable)
	updateMobileTarget();
	render();
})();
